import Decimal from 'decimal.js';
import { compact, flatten } from 'lodash';

import { ActionButtonRenderer } from '@/components/tables/DataTable/renderers/cell/ActionButtonRenderer';
import { EmptyCell } from '@/components/tables/DataTable/renderers/cell/EmptyCell';
import { TextRenderer } from '@/components/tables/DataTable/renderers/cell/TextRenderer';
import { TwoLineTextRenderer } from '@/components/tables/DataTable/renderers/cell/TwoLineTextRenderer';
import { Column } from '@/components/tables/DataTable/types';
import { IntegrationLinkedIcon } from '@/modules/assetProviderIntegrations/shared/IntegrationLinkedIcon';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { isBeneficiaryDesignationEntity } from '@/modules/dispositiveProvisions/dispositiveProvisions.label';
import { InsurancePolicies_InsurancePolicyFragment } from '@/modules/entities/InsurancePolicyDetailsSubform/graphql/InsurancePolicies.generated';
import {
  InsurancePolicyFrequencyCopyMap,
  InsurancePolicyTypeCopyMap,
} from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import {
  DispositiveProvisionRecipientKind,
  EntityKind,
  InsurancePolicyKind,
} from '@/types/schema';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { getNodes } from '@/utils/graphqlUtils';

import { DeleteInsurancePolicyMenuItem } from '../components/DeleteInsurancePolicyMenuItem';
import {
  EstateLifeInsurances_BeneficiaryDesignationsFragment,
  EstateLifeInsurances_DispositionRecipientFragment,
  EstateLifeInsurances_DispositionScenarioFragment,
  EstateLifeInsurances_EntityFragment,
} from '../graphql/EstateLifeInsurances.generated';

export interface LifeInsurancePolicyRowData {
  id: string;
  entity: {
    id: string;
    kind: EntityKind;
    subtype: {
      id: string;
    };
  };
  policy: InsurancePolicies_InsurancePolicyFragment;
  carrierName: string | null | undefined;
  kind: InsurancePolicyKind;
  deathBenefitAmount: Decimal;
  owner: string | undefined;
  insured: string[] | undefined;
  beneficiaries: string[] | undefined;
  cashValue: Decimal | null | undefined;
  isIntegrationLinked: boolean;
  actions?: never;
}

export interface UseMapLifeInsurancePolicyColumnsOptions {
  refetch: () => void;
}

/**
 * Hook that returns the column definitions for the Life Insurance policies table.
 */
export const useMapLifeInsurancePolicyColumns = ({
  refetch,
}: UseMapLifeInsurancePolicyColumnsOptions): Column<LifeInsurancePolicyRowData>[] => {
  const canEdit = useCanEditHouseholdData();

  const columns: Column<LifeInsurancePolicyRowData>[] = [
    {
      field: 'carrierName',
      headerName: 'Policy details',
      width: 300,
      sortable: true,
      renderCell: TwoLineTextRenderer({
        lineOne: ({ row }) => InsurancePolicyTypeCopyMap[row.kind],
        lineTwo: ({ row }) => row.carrierName || 'None',
        textAccompaniment: ({ row }) =>
          row.isIntegrationLinked ? <IntegrationLinkedIcon /> : null,
      }),
      flex: 1,
    },
    {
      field: 'owner',
      headerName: 'Owner',
      width: 300,
      sortable: true,
      renderCell: TextRenderer({
        text: ({ row }) => (row.owner ? row.owner : <EmptyCell />),
      }),
      flex: 1,
    },
    {
      field: 'insured',
      headerName: 'Insured',
      width: 300,
      sortable: true,
      valueGetter: (_value, row) => row.insured?.join(', ') ?? '',
      renderCell: TextRenderer({
        text: ({ row }) =>
          row.insured?.length ? row.insured.join(', ') : <EmptyCell />,
      }),
      flex: 1,
    },
    {
      field: 'beneficiaries',
      headerName: 'Primary beneficiaries',
      width: 300,
      sortable: true,
      valueGetter: (_value, row) => row.beneficiaries?.join(', ') ?? '',
      renderCell: TextRenderer({
        text: ({ row }) =>
          row.beneficiaries?.length ? (
            row.beneficiaries.join(', ')
          ) : (
            <EmptyCell />
          ),
      }),
      flex: 1,
    },
    {
      field: 'policy',
      headerName: 'Premium',
      width: 300,
      sortable: false,
      renderCell: TwoLineTextRenderer({
        lineOne: ({ row }) =>
          row.policy.premiumAmount ? (
            formatCurrencyNoDecimals(row.policy.premiumAmount)
          ) : (
            <EmptyCell />
          ),
        lineTwo: ({ row }) =>
          row.policy.premiumFrequency
            ? InsurancePolicyFrequencyCopyMap[row.policy.premiumFrequency]
            : null,
      }),
      flex: 1,
    },
    {
      field: 'cashValue',
      headerName: 'Cash value',
      width: 300,
      sortable: true,
      renderCell: TextRenderer({
        text: ({ row }) =>
          row.cashValue ? (
            formatCurrencyNoDecimals(row.cashValue)
          ) : (
            <EmptyCell />
          ),
      }),
      flex: 1,
    },
    {
      field: 'deathBenefitAmount',
      headerName: 'Death benefit',
      width: 300,
      sortable: true,
      renderCell: TextRenderer({
        text: ({ row }) =>
          formatCurrencyNoDecimals(new Decimal(row.deathBenefitAmount)),
      }),
      flex: 1,
    },
  ];

  if (canEdit) {
    columns.push({
      field: 'actions',
      headerName: '',
      align: 'center',
      width: 64,
      minWidth: 64,
      maxWidth: 64,
      renderCell: ActionButtonRenderer({
        actions: ({ row }) => [
          {
            MenuItem: () => (
              <DeleteInsurancePolicyMenuItem
                policyId={row.id}
                entityId={row.entity.id}
                entityKind={row.entity.kind}
                subtypeId={row.entity.subtype.id}
                onDelete={refetch}
              />
            ),
          },
        ],
      }),
      sortable: false,
    });
  }

  return columns;
};

/**
 * Transforms entity data into rows for the Life Insurance policies table.
 */
export const mapLifeInsurancePolicyRows = (
  data: EstateLifeInsurances_EntityFragment[],
  firstPrimaryClientId: string
): LifeInsurancePolicyRowData[] => {
  return flatten(
    compact(
      data?.map((entity) => {
        const useBeneficiaryDesignations = isBeneficiaryDesignationEntity(
          entity.kind
        );

        if ('policies' in entity.subtype && entity.subtype.policies) {
          return entity.subtype.policies.map((p) => ({
            id: p.id,
            entity: {
              id: entity.id,
              kind: entity.kind,
              subtype: {
                id: entity.subtype.id,
              },
            },
            policy: p,
            kind: p.kind,
            carrierName: p.carrierName,
            deathBenefitAmount: p.deathBenefitAmount,
            cashValue: p.cashValue,
            isIntegrationLinked: !!p.integrationInsurance,
            // the policy_holder is the insured
            insured: p.policyHolders?.map((cp) => cp.displayName),
            // the owner is the entity that holds this policy
            owner: entity.subtype?.displayName,
            // Primary beneficiaries are the people who receive the policy
            // benefit on the insured's death. For BDG-migrated entities they
            // come from the resolved primary designation tier; otherwise we
            // fall back to the entity's (legacy) disposition scenarios.
            beneficiaries: extractPrimaryBeneficiaryNamesForEntity(
              entity,
              firstPrimaryClientId,
              useBeneficiaryDesignations,
              p.policyHolders?.map((ph) => ph.id) ?? [],
              p.survivorship
            ),
          }));
        }
        return null;
      })
    )
  );
};

/**
 * Maps a list of disposition provision recipients to their display names.
 */
const mapRecipientsToDisplayNames = (
  recipients: (EstateLifeInsurances_DispositionRecipientFragment | null)[]
): string[] => {
  return compact(
    recipients.map((r) => {
      if (!r) return '';

      if (
        r.recipientKind === DispositiveProvisionRecipientKind.SurvivingSpouse
      ) {
        return 'Surviving spouse';
      }

      return (
        r.entity?.subtype?.displayName ||
        r.individual?.displayName ||
        r.organization?.name ||
        r.testamentaryEntity?.displayName
      );
    })
  );
};

/**
 * Extracts primary beneficiary names for a policy's owning entity.
 *
 * When the entity reads from beneficiary designations, prefers the resolved
 * primary designation tier (populated for both migrated and live-converted
 * entities) and falls back to the legacy disposition-scenario model when no
 * primary designations exist.
 */
export const extractPrimaryBeneficiaryNamesForEntity = (
  entity: EstateLifeInsurances_EntityFragment,
  firstPrimaryClientId: string,
  useBeneficiaryDesignations: boolean,
  policyHolderIds: string[] = [],
  isSurvivorship: boolean = false
): string[] => {
  if (
    useBeneficiaryDesignations &&
    'beneficiaryDesignations' in entity.subtype
  ) {
    const primaryDesignationNames =
      extractPrimaryBeneficiaryNamesFromBeneficiaryDesignations(
        entity.subtype.beneficiaryDesignations
      );
    if (primaryDesignationNames.length > 0) {
      return primaryDesignationNames;
    }
  }

  return extractPolicyBeneficiaryNamesFromEntityDispositionScenarios(
    entity,
    firstPrimaryClientId,
    policyHolderIds,
    isSurvivorship
  );
};

/**
 * Extracts primary beneficiary names from a resolved BDG designations object.
 */
export const extractPrimaryBeneficiaryNamesFromBeneficiaryDesignations = (
  beneficiaryDesignations:
    | EstateLifeInsurances_BeneficiaryDesignationsFragment
    | null
    | undefined
): string[] => {
  const primaryRecipients = getNodes(
    beneficiaryDesignations?.primary?.dispositiveProvisions
  );
  return mapRecipientsToDisplayNames(primaryRecipients);
};

/**
 * Extracts beneficiary names from an entity's disposition scenarios.
 * Selects the appropriate scenario and provisions based on the policy's
 * insured persons and survivorship flag to determine which death event
 * triggers the insurance payout.
 */
export const extractPolicyBeneficiaryNamesFromEntityDispositionScenarios = (
  entity: EstateLifeInsurances_EntityFragment,
  firstPrimaryClientId: string,
  policyHolderIds: string[] = [],
  isSurvivorship: boolean = false
): string[] => {
  if (!('dispositionScenarios' in entity.subtype)) {
    return [];
  }

  const scenarios = entity.subtype?.dispositionScenarios;
  if (!scenarios || scenarios.length === 0) {
    return [];
  }

  // Select the scenario where the first primary client dies first.
  let selectedScenario:
    | EstateLifeInsurances_DispositionScenarioFragment
    | null
    | undefined = scenarios.find(
    (scenario) => scenario?.firstGrantorDeath?.id === firstPrimaryClientId
  );
  // If that scenario is not found, just use the first scenario.
  if (!selectedScenario) {
    selectedScenario = scenarios[0];
  }

  // If still not found, return empty array.
  if (!selectedScenario) {
    return [];
  }

  const firstDeathRecipients = getNodes(selectedScenario.dispositiveProvisions);
  const secondDeathRecipients = getNodes(
    selectedScenario.secondDeathDispositiveProvisions
  );

  // Determine which provision set to use based on when the insurance pays out.
  // Survivorship policies pay on last (second) death.
  // Otherwise, if any insured person is the first to die in this scenario,
  // the policy pays at first death; if not, the policy pays at second death.
  const insuredDiesFirst = policyHolderIds.some(
    (id) => id === selectedScenario.firstGrantorDeath?.id
  );
  const preferSecondDeath = isSurvivorship || !insuredDiesFirst;

  const primaryRecipients = preferSecondDeath
    ? secondDeathRecipients
    : firstDeathRecipients;
  const fallbackRecipients = preferSecondDeath
    ? firstDeathRecipients
    : secondDeathRecipients;

  // Use the preferred provision set; fall back to the other if empty.
  const recipients =
    primaryRecipients.length > 0 ? primaryRecipients : fallbackRecipients;

  return mapRecipientsToDisplayNames(recipients);
};
